import {
	Box,
	Button,
	Card,
	CardActions,
	Grid,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { StaffSimpleDetailDto } from '@/api/DTO/user-management/userManagementList.interface';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { LANGUAGE_LIST, YESNO } from '@/common/constants/common';
import {
	useGetAreaItemListQuery,
	useGetAreaSubItemListMutation,
	useGetDicListQuery,
} from '@/api/common';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import {
	useEditContactMutation,
	useGetAdmissionRequestDetailQuery,
} from '@/api/member-management/admissionRequest';
import { EditContactRequest } from '@/api/DTO/member-management/admissionRequest';
import { AreaDTO } from '@/api/DTO/common.interface';
import { Language } from '@/api/enum/common.enum';
import { eightDigitPhone } from '@/utils/validationHelper';

interface Props {
	type: string;
}
export default function EditContactInformation(props: Props) {
	const { type } = props;
	const memberType = i18n.t(
		type === 'admissionRequest'
			? 'member.admissionRequestLabel'
			: 'member.memberDetailsLabel'
	);
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { admissionRequestId } = useParams();
	const [editContact] = useEditContactMutation();
	const [getAreaSubItemList] = useGetAreaSubItemListMutation();
	const [districtList, setDistrictList] = useState<AreaDTO[]>([]);
	const [subDistrictList, setSubDistrictList] = useState<AreaDTO[]>([]);
	const [estateCourtVillageList, setEstateCourtVillageList] = useState<
		AreaDTO[]
	>([]);
	const [buildingBlockList, setBuildingBlockList] = useState<AreaDTO[]>([]);

	const { data: details } = useGetAdmissionRequestDetailQuery(
		admissionRequestId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const { data: housingTypeDicList } = useGetDicListQuery('housing_type', {
		refetchOnMountOrArgChange: true,
	});

	const { data: areaItemList } = useGetAreaItemListQuery(
		{ type: 0 },
		{ refetchOnMountOrArgChange: true }
	);

	const defaultValues: DefaultValues<EditContactRequest> = {
		addressLanguage: Language.chinese,
		useFullAddressAsMailAddress: false,
	};

	const formSchema = Yup.object().shape({
		phone: Yup.string()
			.required(t('errorMessage.pleaseEnter'))
			.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
		homeTel: Yup.string()
			.nullable()
			.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
		otherPhone: Yup.string()
			.nullable()
			.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
		addressLanguage: Yup.string().required(t('errorMessage.pleaseSelect')),
		useFullAddressAsMailAddress: Yup.boolean().required(
			t('errorMessage.pleaseSelect')
		),
		mailAddress: Yup.string().when('useFullAddressAsMailAddress', {
			is: (useFullAddressAsMailAddress: string) => !useFullAddressAsMailAddress,
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),
		typeOfHousingSpecify: Yup.string().when('typeOfHousing', {
			is: (typeOfHousing: string) => typeOfHousing && housingTypeIsOther(),
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),
	});
	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
		mode: 'onChange',
		shouldFocusError: true,
	});

	const watchAddressLanguage = methods.watch('addressLanguage');
	const watchTypeOfHousing = methods.watch('typeOfHousing');
	const watchUseFullAddressAsMailAddress = methods.watch(
		'useFullAddressAsMailAddress'
	);
	const housingTypeIsOther = () => {
		const obj = housingTypeDicList?.data.find(
			(item: any) => item.id == watchTypeOfHousing
		);
		if (obj && obj.labelEn === 'Others') return true;
		return false;
	};

	useEffect(() => {
		if (details) {
			methods.reset(details.data);

			if (details.data.region) initAddress(details.data.region, 'region');
			if (details.data.district) initAddress(details.data.district, 'district');
			if (details.data.subdistrict)
				initAddress(details.data.subdistrict, 'subdistrict');
			if (details.data.estateCourtVillage)
				initAddress(details.data.estateCourtVillage, 'estateCourtVillage');
		}
	}, [details]);

	const initAddress = async (pid: string, type: string) => {
		if (!pid) return;
		const response = await getAreaSubItemList(pid || '');
		if ('data' in response) {
			if (type == 'region') setDistrictList(response?.data?.data || []);
			if (type == 'district') setSubDistrictList(response?.data?.data || []);
			if (type == 'subdistrict')
				setEstateCourtVillageList(response?.data?.data || []);
			if (type == 'estateCourtVillage')
				setBuildingBlockList(response?.data?.data || []);
		}
	};

	const handleChangeRegion = async (e: any, type: string) => {
		if (!e) return;
		const response = await getAreaSubItemList(e || '');

		if ('data' in response) {
			if (type == 'region') {
				methods.setValue('district', '');
				methods.setValue('subdistrict', '');
				methods.setValue('estateCourtVillage', '');
				methods.setValue('buildingBlock', '');
				setDistrictList(response?.data?.data || []);
			}
			if (type == 'district') {
				methods.setValue('subdistrict', '');
				methods.setValue('estateCourtVillage', '');
				methods.setValue('buildingBlock', '');
				setSubDistrictList(response?.data?.data || []);
			}
			if (type == 'subdistrict') {
				methods.setValue('estateCourtVillage', '');
				methods.setValue('buildingBlock', '');
				setEstateCourtVillageList(response?.data?.data || []);
			}
			if (type == 'estateCourtVillage') {
				methods.setValue('buildingBlock', '');
				setBuildingBlockList(response?.data?.data || []);
			}
		}
	};

	const onSubmit: SubmitHandler<EditContactRequest> = async (data) => {
		const obj: EditContactRequest = { ...data };
		const response = await editContact(obj);

		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateMemberSuccess', { type: memberType }), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			navigate(
				`/member-management/${
					type == 'member' ? 'member-list' : 'admission-request-list'
				}/detail/${admissionRequestId}`
			);
		}
	};

	const handleClose = () => {
		navigate(
			`/member-management/${
				type == 'member' ? 'member-list' : 'admission-request-list'
			}/detail/${admissionRequestId}`,
			{ replace: true }
		);
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<FormProvider {...methods}>
					<form onSubmit={methods.handleSubmit(onSubmit)}>
						<Box display='flex' justifyContent='space-between'>
							<Typography variant='h4'>
								{type == 'admissionRequest'
									? t('drawerListItems.editAdmissionRequest')
									: t('drawerListItems.editMember')}
							</Typography>
						</Box>
						<Card sx={{ padding: '24px 24px', marginTop: 3 }}>
							<Box
								display='flex'
								justifyContent='space-between'
								marginBottom={2}
							>
								<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
									{t('member.contactInformation')}
								</Typography>
							</Box>
							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid size={6}>
									<AppTextField
										control={methods.control}
										name='phone'
										label={t('preAdmission.mobilePhone') + '*'}
									/>
								</Grid>
								<Grid size={6}>
									<AppTextField
										control={methods.control}
										name='homeTel'
										label={t('preAdmission.homeTel')}
									/>
								</Grid>
								<Grid size={6}>
									<AppTextField
										control={methods.control}
										name='otherPhone'
										label={t('preAdmission.otherPhone')}
									/>
								</Grid>
								<Grid size={6}>
									<AppTextField
										control={methods.control}
										name='email'
										label={t('common.email')}
									/>
								</Grid>
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='typeOfHousing'
										label={t('preAdmission.typeHousing')}
										options={(housingTypeDicList?.data || []).map(
											(item: { labelEn: any }) => {
												return {
													...item,
													name: item.labelEn,
												};
											}
										)}
										handleChange={() => {
											methods.setValue('typeOfHousingSpecify', '');

											methods.clearErrors('typeOfHousingSpecify');
										}}
									/>
								</Grid>
								<Grid size={6}>
									<AppTextField
										disabled={!housingTypeIsOther()}
										control={methods.control}
										name='typeOfHousingSpecify'
										label={`${t('preAdmission.typeHousingSpecify')}${
											housingTypeIsOther() ? '*' : ''
										}`}
									/>
								</Grid>
								<Grid size={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('preAdmission.addressLanguage') + '*'}
									</Typography>
									<AppRadioGroup
										control={methods.control}
										name='addressLanguage'
										row
										options={LANGUAGE_LIST}
									/>
								</Grid>
								{watchAddressLanguage == Language.english && (
									<>
										<Grid size={6}>
											<AppDropDownMenu
												optional={true}
												control={methods.control}
												name='region'
												label={t('preAdmission.region')}
												options={(areaItemList?.data || []).map(
													(item: { nameEn: any }) => {
														return {
															...item,
															name: item.nameEn,
														};
													}
												)}
												handleChange={(e: any) => {
													handleChangeRegion(e, 'region');
												}}
											/>
										</Grid>
										<Grid size={6}>
											<AppDropDownMenu
												optional={true}
												control={methods.control}
												name='district'
												label={t('preAdmission.district')}
												options={(districtList || []).map((item: AreaDTO) => {
													return {
														...item,
														name: item.nameEn,
													};
												})}
												handleChange={(e: any) => {
													handleChangeRegion(e, 'district');
												}}
											/>
										</Grid>
										<Grid size={6}>
											<AppDropDownMenu
												optional={true}
												control={methods.control}
												name='subdistrict'
												label={t('preAdmission.subDistrict')}
												options={(subDistrictList || []).map(
													(item: AreaDTO) => {
														return {
															...item,
															name: item.nameEn,
														};
													}
												)}
												handleChange={(e: any) => {
													handleChangeRegion(e, 'subdistrict');
												}}
											/>
										</Grid>
										<Grid size={6}>
											<AppDropDownMenu
												optional={true}
												control={methods.control}
												name='estateCourtVillage'
												label={t('preAdmission.estate/court/village')}
												options={(estateCourtVillageList || []).map(
													(item: AreaDTO) => {
														return {
															...item,
															name: item.nameEn,
														};
													}
												)}
												handleChange={(e: any) => {
													handleChangeRegion(e, 'estateCourtVillage');
												}}
											/>
										</Grid>
									</>
								)}
								{watchAddressLanguage == Language.chinese && (
									<>
										<Grid size={6}>
											<AppDropDownMenu
												optional={true}
												control={methods.control}
												name='region'
												label={t('preAdmission.region')}
												options={(areaItemList?.data || []).map(
													(item: { nameZh: any }) => {
														return {
															...item,
															name: item.nameZh,
														};
													}
												)}
												handleChange={(e: any) => {
													handleChangeRegion(e, 'region');
												}}
											/>
										</Grid>
										<Grid size={6}>
											<AppDropDownMenu
												optional={true}
												control={methods.control}
												name='district'
												label={t('preAdmission.district')}
												options={(districtList || []).map((item: AreaDTO) => {
													return {
														...item,
														name: item.nameZh,
													};
												})}
												handleChange={(e: any) => {
													handleChangeRegion(e, 'district');
												}}
											/>
										</Grid>
										<Grid size={6}>
											<AppDropDownMenu
												optional={true}
												control={methods.control}
												name='subdistrict'
												label={t('preAdmission.subDistrict')}
												options={(subDistrictList || []).map(
													(item: AreaDTO) => {
														return {
															...item,
															name: item.nameZh,
														};
													}
												)}
												handleChange={(e: any) => {
													handleChangeRegion(e, 'subdistrict');
												}}
											/>
										</Grid>
										<Grid size={6}>
											<AppDropDownMenu
												optional={true}
												control={methods.control}
												name='estateCourtVillage'
												label={t('preAdmission.estate/court/village')}
												options={(estateCourtVillageList || []).map(
													(item: AreaDTO) => {
														return {
															...item,
															name: item.nameZh,
														};
													}
												)}
												handleChange={(e: any) => {
													handleChangeRegion(e, 'estateCourtVillage');
												}}
											/>
										</Grid>
									</>
								)}

								<Grid size={6}>
									<AppTextField
										control={methods.control}
										name='roadStreet'
										label={t('preAdmission.road/street')}
									/>
								</Grid>
								<Grid size={6}>
									<AppTextField
										control={methods.control}
										name='roadStreetNo'
										label={t('preAdmission.road/streetNo')}
									/>
								</Grid>
								{watchAddressLanguage == Language.english && (
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='buildingBlock'
											label={t('preAdmission.building/block')}
											options={(buildingBlockList || []).map(
												(item: AreaDTO) => {
													return {
														...item,
														name: item.nameEn,
													};
												}
											)}
										/>
									</Grid>
								)}
								{watchAddressLanguage == Language.chinese && (
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='buildingBlock'
											label={t('preAdmission.building/block')}
											options={(buildingBlockList || []).map(
												(item: AreaDTO) => {
													return {
														...item,
														name: item.nameZh,
													};
												}
											)}
										/>
									</Grid>
								)}

								<Grid size={6}>
									<AppTextField
										control={methods.control}
										name='buildingBlockSpecify'
										label={t('preAdmission.building/block(specify)')}
									/>
								</Grid>
								<Grid size={6}>
									<AppTextField
										control={methods.control}
										name='floor'
										label={t('preAdmission.floor')}
									/>
								</Grid>
								<Grid size={6}>
									<AppTextField
										control={methods.control}
										name='flat'
										label={t('preAdmission.flat')}
									/>
								</Grid>
								<Grid size={6}>
									<Typography variant='body2' color='common.neutral40'>
										{t('member.useFullAddressAsMailAddress') + '*'}
									</Typography>
									<AppRadioGroup
										control={methods.control}
										name='useFullAddressAsMailAddress'
										row
										options={YESNO}
										handleChange={(e: any) => {
											methods.setValue('mailAddress', '');
											methods.clearErrors('mailAddress');
										}}
									/>
								</Grid>
								<Grid size={6}>
									<AppTextField
										control={methods.control}
										label={`${t('member.mailAddress')}${
											watchUseFullAddressAsMailAddress === 'false' ? '*' : ''
										}`}
										name={'mailAddress'}
										disabled={watchUseFullAddressAsMailAddress === 'true'}
									/>
								</Grid>
							</Grid>
							<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
								<Button onClick={handleClose}>{t('button.cancel')}</Button>
								<Button
									type='submit'
									variant='contained'
									endIcon={<CheckCircleOutlineIcon />}
								>
									{t('button.complete')}
								</Button>
							</CardActions>
						</Card>
					</form>
				</FormProvider>
			</Box>
		</>
	);
}
